import { useContext, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../utils/auth";
import { v4 as uuidv4 } from "uuid";
import { API_BASE } from "../shared/Constants";
import { reportHandledError, reportApiError } from "../utils/reportError";
import { SideMenuContext } from "../contexts/SideMenuContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export interface HelpBody {
  name: string;
  contact_info: string;
  date: string;
  subject: string;
  message: string;
}

export default function HelpForm() {
  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const subjectInputRef = useRef<HTMLSelectElement>(null);
  const [subjects, setSubjects] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const { user, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { setOpenHelp, setToastMessage } = useContext(SideMenuContext);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/subjects`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        const data = await response.json();
        if (response.ok) setSubjects(data["subjects"]);
        else {
          reportApiError(response, { feature: "help.fetch", method: "GET" });
          setToastMessage({
            type: "error",
            message: "Erro ao carregar assuntos de ajuda",
            id: uuidv4(),
          });
        }
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "help.fetch" });
        setToastMessage({
          type: "error",
          message: "Erro ao carregar assuntos de ajuda",
          id: uuidv4(),
        });
      }
    };
    fetchSubjects();
  }, [setToastMessage]);

  const sendHelpRequest = async () => {
    const name = nameInputRef.current?.value?.trim();
    const email = emailInputRef.current?.value?.trim();
    const subject = subjectInputRef.current?.value;
    const message = messageInputRef.current?.value?.trim();

    if (!name || !email || !subject || !message) {
      setToastMessage({
        type: "error",
        message: "Preencha todos os campos obrigatórios.",
        id: uuidv4(),
      });
      return;
    }

    const body: HelpBody = {
      name,
      contact_info: email,
      date: new Date().toISOString(),
      subject,
      message,
    };

    setSubmitting(true);
    try {
      const access_token = user ? (await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect })) || "" : "";
      const response = await fetch(`${API_BASE}/api/help`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(access_token ? { Authorization: `Auth ${access_token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        setOpenHelp(false);
        setToastMessage({
          type: "success",
          message: "Solicitação enviada com sucesso. Em breve entraremos em contato.",
          id: uuidv4(),
        });
      } else {
        reportApiError(response, { feature: "help.submit", method: "POST" });
        setToastMessage({
          type: "error",
          message: "Erro ao enviar solicitação. Tente novamente.",
          id: uuidv4(),
        });
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "help.submit" });
      setToastMessage({
        type: "error",
        message: "Erro ao enviar solicitação. Tente novamente.",
        id: uuidv4(),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="help-name">Nome completo *</Label>
          <Input
            id="help-name"
            ref={nameInputRef}
            type="text"
            required
            maxLength={70}
            placeholder="Seu nome completo"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="help-email">E-mail *</Label>
          <Input
            id="help-email"
            ref={emailInputRef}
            type="email"
            required
            maxLength={100}
            placeholder="seu@email.com"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="help-subject">Assunto *</Label>
        <select
          id="help-subject"
          ref={subjectInputRef}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <option value="">Selecione um assunto</option>
          {subjects.map((s, i) => (
            <option key={i} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="help-message">Mensagem *</Label>
        <textarea
          id="help-message"
          ref={messageInputRef}
          required
          maxLength={1000}
          placeholder="Descreva seu problema ou dúvida detalhadamente..."
          rows={5}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-foreground/40 focus-visible:ring-1 focus-visible:ring-foreground/10 disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      <div className="flex justify-end">
        <Button onClick={sendHelpRequest} disabled={submitting}>
          {submitting ? "Enviando..." : "Enviar solicitação"}
        </Button>
      </div>
    </div>
  );
}